import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { COMMISSION_CATEGORY_TYPES, REDEEM_TYPES, commissionCategorySchema } from '@app/shared';
import type {
  CommissionCategoryDto,
  CommissionCategoryInput,
  CommissionLimitDto,
  RedeemRuleDto,
  RedeemType,
} from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { FormDialog } from '../../components/dialogs';
import { ApiError, api } from '../../lib/api';
import { formatCents, formatDate } from '../../lib/format';
import { useFeatureReleased } from '../../release';
import { useUserOptions } from './api';

const BASE = '/commission-matrices';
const toCents = (text: string) => Math.round(Number(text) * 100);
const fieldError = (err: unknown, path: string) =>
  err instanceof ApiError ? err.details.find((d) => d.path === path)?.message : undefined;

/** "View category": the commission categories (two built in, then the added ones). */
function CategoryDialog({ admin, onClose }: { admin: boolean; onClose: () => void }) {
  const featureOn = useFeatureReleased();
  const adding = featureOn('commission-add-category');
  const [add, setAdd] = useState(false);
  const list = useQuery({
    queryKey: ['commission', 'categories'],
    queryFn: () => api.get<{ data: CommissionCategoryDto[] }>(`${BASE}/categories`),
    select: (r) => r.data,
  });
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>View category</DialogTitle>
      <DialogContent>
        <Table size="small" aria-label="Commission categories">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Category name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Amount</TableCell>
              {adding ? (
                <>
                  <TableCell>Created by</TableCell>
                  <TableCell>Created date</TableCell>
                </>
              ) : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {(list.data ?? []).map((c, i) => (
              <TableRow key={c.name}>
                <TableCell>{i + 1}</TableCell>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.type}</TableCell>
                <TableCell align="right">{formatCents(c.amountCents)}</TableCell>
                {adding ? (
                  <>
                    <TableCell>{c.createdBy}</TableCell>
                    <TableCell>{c.createdAt ? formatDate(c.createdAt) : ''}</TableCell>
                  </>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
          {adding && admin ? (
            <Button variant="contained" onClick={() => setAdd(true)}>
              Add Category
            </Button>
          ) : null}
          <Button onClick={onClose}>Close</Button>
        </Box>
      </DialogContent>
      {add ? <AddCategoryDialog onClose={() => setAdd(false)} /> : null}
    </Dialog>
  );
}

/**
 * "Add Category" (switch commission-add-category): Category name*, Type*, Amount*. What an added
 * category earns is 212Ez's reading, said under the form.
 */
function AddCategoryDialog({ onClose }: { onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [amount, setAmount] = useState('');
  const [problems, setProblems] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: (body: CommissionCategoryInput) =>
      api.post<CommissionCategoryDto>(`${BASE}/categories`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['commission'] }),
  });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = {
      name,
      type,
      amountCents: amount.trim() === '' || Number.isNaN(Number(amount)) ? NaN : toCents(amount),
    };
    const parsed = commissionCategorySchema.safeParse(body);
    if (!parsed.success) {
      setProblems(
        Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])),
      );
      return;
    }
    try {
      await save.mutateAsync(parsed.data);
      notify.success('Category added.');
      onClose();
    } catch (err) {
      const fields = ['name', 'type', 'amountCents'];
      const found = Object.fromEntries(
        fields.map((f) => [f, fieldError(err, f) ?? '']).filter(([, m]) => m),
      );
      setProblems(found);
      if (Object.keys(found).length === 0) notify.error(err);
    }
  };
  return (
    <FormDialog
      open
      title="Add Category"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={save.isPending}
      submitLabel="Submit"
      cancelLabel="Close"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        <TextField
          required
          label="Category name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!problems.name}
          helperText={problems.name}
          slotProps={{ htmlInput: { maxLength: 50 } }}
        />
        <TextField
          select
          required
          label="Type"
          value={type}
          onChange={(e) => setType(e.target.value)}
          error={!!problems.type}
          helperText={problems.type}
        >
          {COMMISSION_CATEGORY_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          required
          label="Amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          error={!!problems.amountCents}
          helperText={problems.amountCents}
          slotProps={{ htmlInput: { inputMode: 'decimal', maxLength: 5 } }}
        />
        <Typography variant="body2" color="text.secondary">
          Each time this category&apos;s event happens, every user of the selling chain who has an
          Active commission matrix earns this amount on top of the matrix: Enrollment, a completed
          enrollment; TopUp, a top-up; Data topUp, a top-up that adds data only; Bill Pay, a plan
          purchase. Default fee and Other are listed only.
        </Typography>
      </Box>
    </FormDialog>
  );
}

/** "Set Matrix Commission Redeem Rule": Manual or Automatic above a threshold. */
function RedeemRuleDialog({ onClose }: { onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const rule = useQuery({
    queryKey: ['commission', 'redeem-rule'],
    queryFn: () => api.get<RedeemRuleDto>(`${BASE}/redeem-rule`),
  });
  const [type, setType] = useState<RedeemType | ''>('');
  const [amount, setAmount] = useState('');
  const [problem, setProblem] = useState('');
  useEffect(() => {
    if (rule.data) {
      setType(rule.data.redeemType);
      setAmount(
        rule.data.thresholdCents === null ? '' : (rule.data.thresholdCents / 100).toFixed(2),
      );
    }
  }, [rule.data]);
  const save = useMutation({
    mutationFn: (body: { redeemType: RedeemType; thresholdCents: number | null }) =>
      api.put<RedeemRuleDto>(`${BASE}/redeem-rule`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['commission'] }),
  });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!type) return setProblem('Select Redeem Type');
    const value = Number(amount);
    if (type === 'Automatic' && (amount.trim() === '' || !(value >= 0 && value <= 10000)))
      return setProblem('Please enter a valid amount (0 - 10,000).');
    try {
      await save.mutateAsync({
        redeemType: type,
        thresholdCents: type === 'Automatic' ? toCents(amount) : null,
      });
      notify.success('Redeem rule saved.');
      onClose();
    } catch (err) {
      setProblem(fieldError(err, 'thresholdCents') ?? '');
      if (!fieldError(err, 'thresholdCents')) notify.error(err);
    }
  };
  return (
    <FormDialog
      open
      title="Set Matrix Commission Redeem Rule"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={save.isPending}
      cancelLabel="Close"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        <TextField
          select
          required
          label="Redeem Type"
          value={type}
          onChange={(e) => {
            setType(e.target.value as RedeemType);
            setProblem('');
          }}
        >
          {REDEEM_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>
        {type === 'Automatic' ? (
          <TextField
            required
            label="Auto Redeem Threshold Amount($)"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setProblem('');
            }}
            helperText="Minimum commission balance required to trigger automatic transfer to the wallet."
            slotProps={{ htmlInput: { inputMode: 'decimal', maxLength: 9 } }}
          />
        ) : null}
        {problem ? <Alert severity="error">{problem}</Alert> : null}
        {type === 'Manual' ? (
          <Typography variant="body2" color="text.secondary">
            The user will need to manually redeem their earned commission in order to transfer it to
            their wallet.
          </Typography>
        ) : null}
        {type === 'Automatic' ? (
          <Typography variant="body2" color="text.secondary">
            The system will automatically transfer the commission to the user&apos;s wallet once the
            commission amount is greater than or equal to the Auto Redeem Threshold Amount.
          </Typography>
        ) : null}
      </Box>
    </FormDialog>
  );
}

/** "Set Commission Limit Rule": per master agent, a percentage and a flat amount. */
function LimitRuleDialog({ onClose }: { onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const masters = useUserOptions('master', undefined);
  const limits = useQuery({
    queryKey: ['commission', 'limits'],
    queryFn: () => api.get<{ data: CommissionLimitDto[] }>(`${BASE}/limits`),
    select: (r) => r.data,
  });
  const [master, setMaster] = useState('');
  const [percent, setPercent] = useState('');
  const [flat, setFlat] = useState('');
  const [problems, setProblems] = useState<Record<string, string>>({});
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['commission', 'limits'] });
  const save = useMutation({
    mutationFn: (body: { masterUserId: string; percentBp: number; flatCents: number }) =>
      api.put(`${BASE}/limits`, body),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`${BASE}/limits/${id}`),
    onSuccess: refresh,
  });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const found: Record<string, string> = {};
    if (!master) found.masterUserId = 'Select Master Agent';
    const p = Number(percent);
    const f = Number(flat);
    if (percent.trim() === '' || !(p >= 0 && p <= 200)) found.percentBp = 'Max allowed is 200%.';
    if (flat.trim() === '' || !(f >= 0 && f <= 100000)) found.flatCents = 'Max allowed is 100000.';
    setProblems(found);
    if (Object.keys(found).length) return;
    try {
      await save.mutateAsync({
        masterUserId: master,
        percentBp: Math.round(p * 100),
        flatCents: toCents(flat),
      });
      notify.success('Commission limit saved.');
      setMaster('');
      setPercent('');
      setFlat('');
    } catch (err) {
      const next = {
        masterUserId: fieldError(err, 'masterUserId') ?? '',
        percentBp: fieldError(err, 'percentBp') ?? '',
        flatCents: fieldError(err, 'flatCents') ?? '',
      };
      setProblems(next);
      if (!Object.values(next).some(Boolean)) notify.error(err);
    }
  };
  return (
    <FormDialog
      open
      title="Set Commission Limit Rule"
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={save.isPending}
      cancelLabel="Close"
      maxWidth="md"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1, gridTemplateColumns: { sm: '2fr 1fr 1fr' } }}>
        <TextField
          select
          required
          label="Master Agent"
          value={master}
          onChange={(e) => setMaster(e.target.value)}
          error={Boolean(problems.masterUserId)}
          helperText={problems.masterUserId || undefined}
        >
          {(masters.data ?? []).map((m) => (
            <MenuItem key={m.id} value={m.id}>
              {m.name} ({m.loginId})
            </MenuItem>
          ))}
        </TextField>
        <TextField
          required
          label="Percentage (%)"
          value={percent}
          onChange={(e) => setPercent(e.target.value)}
          error={Boolean(problems.percentBp)}
          helperText={problems.percentBp || 'Max allowed is 200%.'}
          slotProps={{ htmlInput: { inputMode: 'decimal', maxLength: 6 } }}
        />
        <TextField
          required
          label="Flat Amount"
          value={flat}
          onChange={(e) => setFlat(e.target.value)}
          error={Boolean(problems.flatCents)}
          helperText={problems.flatCents || 'Max allowed is 100000.'}
          slotProps={{ htmlInput: { inputMode: 'decimal', maxLength: 9 } }}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        Commission Limits: Both percentage and flat amount limits can be set. The sum of the values
        (flat amount and percentage of plan price) is the most one sale earns any user of that
        master agent&apos;s chain. Percentage: 0% to 200% | Flat Amount: 0 to 100,000
      </Typography>
      <Typography variant="h3" sx={{ mt: 2, mb: 1 }}>
        Master Agent Commission Limits
      </Typography>
      {limits.data && limits.data.length === 0 ? (
        <Typography variant="body2">
          No Commission Limits Set. Use the form above to set commission limits for master agents.
        </Typography>
      ) : (
        <Table size="small" aria-label="Master Agent Commission Limits">
          <TableHead>
            <TableRow>
              <TableCell>Master Agent</TableCell>
              <TableCell align="right">Percentage (%)</TableCell>
              <TableCell align="right">Flat Amount</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(limits.data ?? []).map((l) => (
              <TableRow key={l.master.id}>
                <TableCell>
                  {l.master.name} ({l.master.loginId})
                </TableCell>
                <TableCell align="right">{(l.percentBp / 100).toFixed(2)}</TableCell>
                <TableCell align="right">{formatCents(l.flatCents)}</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    aria-label={`Remove the limit of ${l.master.loginId}`}
                    onClick={() =>
                      void remove.mutateAsync(l.master.id).catch((err) => notify.error(err))
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </FormDialog>
  );
}

/** The three buttons of the walk of 2026-10-27 on Manage Commission Matrix (switch commission-redeem). */
export function CommissionRuleButtons({ admin }: { admin: boolean }) {
  const [open, setOpen] = useState<'category' | 'redeem' | 'limit' | null>(null);
  return (
    <>
      <Button variant="outlined" onClick={() => setOpen('category')}>
        View category
      </Button>
      {admin ? (
        <>
          <Button variant="outlined" onClick={() => setOpen('redeem')}>
            Set Redeem Rule
          </Button>
          <Button variant="outlined" onClick={() => setOpen('limit')}>
            Set Commission Limit Rule
          </Button>
        </>
      ) : null}
      {open === 'category' ? <CategoryDialog admin={admin} onClose={() => setOpen(null)} /> : null}
      {open === 'redeem' ? <RedeemRuleDialog onClose={() => setOpen(null)} /> : null}
      {open === 'limit' ? <LimitRuleDialog onClose={() => setOpen(null)} /> : null}
    </>
  );
}
